// ============================================================================
// NEUROLOGY CONCEPTS (Phase L-2 — first specialty branch)
//
// specialtyId = 'neurology'. The neurology branch off the shared main current.
// This is NOT a neurology ontology — it is a ReferralGate operational evidence
// library for referral intake, scoped to current + near-future Neurology Packs.
//
// CLASSIFICATION (L-2 contract — docs/L-2-neurology-concept-library-contract.md):
//   • LIVE_LINKABLE (8): 6 referral-reason concepts (promoted to LIVE with
//       TIGHTENED recognition) ride the existing FINDING group; 2 new red flags
//       (thunderclap_headache, status_epilepticus) ride the existing RED_FLAG
//       group. Recognition patterns live in `recognitionPatterns` below; group
//       membership is appended in index.js → ZERO evidenceAutoLink logic change.
//   • DATA_ONLY (20): present + governed, NO recognition pattern, NOT in any
//       group → never auto-links / surfaces. Red-flag DATA_ONLY concepts carry
//       full safety boundaries so promotion is a one-line group/pattern add.
//   • DELAYED_RISKY (not added): broad symptoms (weakness/numbness/dizziness/
//       confusion/pain/visual_symptoms/memory_problems), high-acuity routing, and
//       all redirect/exclusion concepts — the latter because there is NO REDIRECT
//       target type; redirect is pathway-engine logic, not evidence-chip logic.
//
// SAFETY: findings get NO match-time negation (only red flags suppress), so the
// LIVE finding aliases are deliberately tightened (no "head pain"/"pain"/"fit"/
// "loss of consciousness"/"rigidity"/bare "shaking"). focal_neurological_deficit
// is intentionally NOT added here — the shared `neurological_deficit` concept
// (L-1, DATA_ONLY) is the single home for that meaning (no duplication).
//
// PURE / Never-PHI. Concept records are pure JSON data; recognition patterns are
// a separate code-level RegExp table (flags 'i' only → stateless).
// ============================================================================

import { makeConcept, CONCEPT_TYPES } from './conceptBuilder.js';

export const concepts = [
  // ══════════════════════════════════════════════════════════════════════════
  // LIVE_LINKABLE — referral-reason concepts (FINDING group, FIELD + OPTION)
  // Promoted from L-1 data-only with tightened aliases.
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'headache', conceptType: CONCEPT_TYPES.SYMPTOM,
    specialtyId: 'neurology',
    canonicalLabel: 'Headache',
    description: 'Headache / migraine referral reason (neurology).',
    aliases: ['headache', 'migraine', 'recurrent headache', 'severe headache', 'cephalgia'],
    preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
    negativePhrases: ['no headache', 'denies headache'],
  }),
  makeConcept({
    conceptId: 'seizure', conceptType: CONCEPT_TYPES.SYMPTOM,
    specialtyId: 'neurology',
    canonicalLabel: 'Seizure',
    description: 'Seizure / epileptic event referral reason (neurology).',
    aliases: ['seizure', 'seizure event', 'epileptic seizure', 'convulsion'],
    preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE', 'DISCHARGE_SUMMARY'],
    negativePhrases: ['no seizure', 'denies seizure'],
  }),
  makeConcept({
    conceptId: 'neuropathy', conceptType: CONCEPT_TYPES.DIAGNOSIS,
    specialtyId: 'neurology',
    canonicalLabel: 'Peripheral neuropathy',
    description: 'Peripheral / sensory neuropathy referral reason (neurology).',
    aliases: ['neuropathy', 'peripheral neuropathy', 'sensory neuropathy', 'polyneuropathy', 'nerve pain'],
    preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'movement_disorder', conceptType: CONCEPT_TYPES.DIAGNOSIS,
    specialtyId: 'neurology',
    canonicalLabel: 'Movement disorder',
    description: 'Tremor / parkinsonism movement disorder referral reason (neurology).',
    aliases: ['movement disorder', 'tremor', 'parkinsonism', 'bradykinesia', 'involuntary movements'],
    preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'cognitive_decline', conceptType: CONCEPT_TYPES.SYMPTOM,
    specialtyId: 'neurology',
    canonicalLabel: 'Cognitive decline',
    description: 'Cognitive decline / impairment referral reason (neurology).',
    aliases: ['cognitive decline', 'cognitive impairment', 'memory decline', 'memory loss', 'dementia'],
    preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
    negativePhrases: ['no cognitive decline', 'cognition intact'],
  }),
  makeConcept({
    conceptId: 'demyelinating_disease', conceptType: CONCEPT_TYPES.DIAGNOSIS,
    specialtyId: 'neurology',
    canonicalLabel: 'Demyelinating disease',
    description: 'Suspected MS / demyelinating disease referral reason (neurology).',
    aliases: ['demyelinating disease', 'demyelination', 'multiple sclerosis', 'MS', 'demyelinating lesion', 'optic neuritis', 'clinically isolated syndrome'],
    abbreviations: ['MS', 'CIS'],
    preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE', 'IMAGING_REPORT'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // LIVE_LINKABLE — red flags (RED_FLAG group, positive_only). New concepts.
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'thunderclap_headache', conceptType: CONCEPT_TYPES.RED_FLAG,
    specialtyId: 'neurology',
    canonicalLabel: 'Thunderclap headache',
    description: 'Thunderclap headache — sudden maximal-onset severe headache (SAH concern).',
    positivePhrases: ['thunderclap headache', 'sudden worst headache of life', 'maximal intensity at onset'],
    negativePhrases: ['no thunderclap headache', 'denies sudden severe headache', 'no sudden onset headache'],
    uncertaintyPhrases: ['possible sudden onset headache', 'severe headache but gradual onset', 'unclear onset'],
    excludedPhrases: ['chronic migraine', 'longstanding headache pattern', 'recurrent typical migraine'],
    commonFalseMatches: ['chronic severe headache', 'migraine flare', 'gradual worsening headache'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER', 'DISCHARGE_SUMMARY'],
  }),
  makeConcept({
    conceptId: 'status_epilepticus', conceptType: CONCEPT_TYPES.RED_FLAG,
    specialtyId: 'neurology',
    canonicalLabel: 'Status epilepticus',
    description: 'Status epilepticus — prolonged or recurrent seizures without recovery.',
    positivePhrases: ['status epilepticus', 'prolonged seizure', 'seizure lasting more than 5 minutes', 'recurrent seizures without recovery'],
    negativePhrases: ['no status epilepticus', 'seizure resolved', 'no prolonged seizure'],
    uncertaintyPhrases: ['possible prolonged seizure', 'unclear seizure duration', 'suspected status epilepticus'],
    excludedPhrases: ['remote seizure history', 'single brief seizure', 'seizure disorder history only'],
    commonFalseMatches: ['history of epilepsy', 'seizure medication review'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER', 'DISCHARGE_SUMMARY'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // DATA_ONLY — symptoms / findings (no pattern, no group → inert)
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'paresthesia', conceptType: CONCEPT_TYPES.SYMPTOM,
    specialtyId: 'neurology',
    canonicalLabel: 'Paresthesia',
    description: 'Paresthesia / tingling sensory symptom.',
    aliases: ['paresthesia', 'paraesthesia', 'tingling', 'pins and needles'],
    preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'gait_instability', conceptType: CONCEPT_TYPES.SYMPTOM,
    specialtyId: 'neurology',
    canonicalLabel: 'Gait instability',
    description: 'Gait instability / ataxia / balance symptom.',
    aliases: ['gait instability', 'unsteady gait', 'balance problems', 'ataxia'],
    preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'speech_disturbance', conceptType: CONCEPT_TYPES.SYMPTOM,
    specialtyId: 'neurology',
    canonicalLabel: 'Speech disturbance',
    description: 'Dysarthria / aphasia speech symptom.',
    aliases: ['speech disturbance', 'dysarthria', 'slurred speech', 'aphasia'],
    preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'visual_disturbance', conceptType: CONCEPT_TYPES.SYMPTOM,
    specialtyId: 'neurology',
    canonicalLabel: 'Visual disturbance',
    description: 'Visual disturbance (blurring / diplopia) symptom.',
    aliases: ['visual disturbance', 'blurred vision', 'double vision', 'diplopia'],
    preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'vertigo', conceptType: CONCEPT_TYPES.SYMPTOM,
    specialtyId: 'neurology',
    canonicalLabel: 'Vertigo',
    description: 'Vertigo / spinning sensation symptom.',
    aliases: ['vertigo', 'spinning sensation'],
    preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'optic_neuritis', conceptType: CONCEPT_TYPES.DIAGNOSIS,
    specialtyId: 'neurology',
    canonicalLabel: 'Optic neuritis',
    description: 'Optic neuritis (optic nerve inflammation).',
    aliases: ['optic neuritis', 'optic nerve inflammation'],
    preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE', 'CONSULT_NOTE'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // DATA_ONLY — measurements / scales (need a measurement/DATE group, deferred)
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'seizure_frequency', conceptType: CONCEPT_TYPES.MEASUREMENT,
    specialtyId: 'neurology',
    canonicalLabel: 'Seizure frequency',
    description: 'How often seizures occur.',
    aliases: ['seizure frequency', 'seizures per month', 'seizure count'],
    valueType: 'number',
    evidenceMode: 'evidence_only',
    requiresNearbyValue: false,
    compatibleTargetTypes: ['FIELD'],
    compatibleFieldTypes: ['number', 'select'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER'],
  }),
  makeConcept({
    conceptId: 'episode_duration', conceptType: CONCEPT_TYPES.MEASUREMENT,
    specialtyId: 'neurology',
    canonicalLabel: 'Episode duration',
    description: 'How long an event / episode lasted.',
    aliases: ['episode duration', 'duration of episode', 'event duration'],
    valueType: 'text',
    evidenceMode: 'keyword',
    requiresNearbyValue: false,
    compatibleTargetTypes: ['FIELD', 'OPTION'],
    compatibleFieldTypes: ['select', 'text', 'number'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER'],
  }),
  makeConcept({
    conceptId: 'headache_severity', conceptType: CONCEPT_TYPES.MEASUREMENT,
    specialtyId: 'neurology',
    canonicalLabel: 'Headache severity',
    description: 'Headache severity / intensity score.',
    aliases: ['headache severity', 'headache score', 'headache intensity'],
    valueType: 'number',
    evidenceMode: 'evidence_only',
    requiresNearbyValue: false,
    compatibleTargetTypes: ['FIELD'],
    compatibleFieldTypes: ['number', 'severity', 'slider'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // DATA_ONLY — documents (map to OTHER/CLINICAL_NOTE; no enum expansion;
  // need a DOCUMENT recognition group, deferred)
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'eeg_report', conceptType: CONCEPT_TYPES.DOCUMENT,
    specialtyId: 'neurology',
    canonicalLabel: 'EEG report',
    description: 'Electroencephalogram report (maps to OTHER).',
    aliases: ['EEG report', 'EEG', 'electroencephalogram'],
    preferredDocumentTypes: ['OTHER'],
    acceptableDocumentTypes: ['CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'emg_report', conceptType: CONCEPT_TYPES.DOCUMENT,
    specialtyId: 'neurology',
    canonicalLabel: 'EMG report',
    description: 'Electromyography report (maps to OTHER).',
    aliases: ['EMG report', 'EMG', 'electromyography', 'electromyogram'],
    preferredDocumentTypes: ['OTHER'],
    acceptableDocumentTypes: ['CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'nerve_conduction_study', conceptType: CONCEPT_TYPES.DOCUMENT,
    specialtyId: 'neurology',
    canonicalLabel: 'Nerve conduction study',
    description: 'Nerve conduction study report (maps to OTHER).',
    aliases: ['nerve conduction study', 'NCS', 'nerve conduction'],
    preferredDocumentTypes: ['OTHER'],
    acceptableDocumentTypes: ['CLINICAL_NOTE'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // DATA_ONLY — medication / exposure (need a MED group, deferred)
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'antiseizure_medication', conceptType: CONCEPT_TYPES.MEDICATION_EXPOSURE,
    specialtyId: 'neurology',
    canonicalLabel: 'Antiseizure medication',
    description: 'Antiseizure / anticonvulsant medication exposure.',
    aliases: ['antiseizure medication', 'anticonvulsant', 'levetiracetam', 'lamotrigine', 'valproate', 'carbamazepine', 'phenytoin', 'keppra'],
    preferredDocumentTypes: ['MEDICATION_LIST', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'migraine_preventive_medication', conceptType: CONCEPT_TYPES.MEDICATION_EXPOSURE,
    specialtyId: 'neurology',
    canonicalLabel: 'Migraine preventive medication',
    description: 'Migraine prophylaxis medication exposure.',
    aliases: ['migraine preventive', 'migraine prophylaxis', 'topiramate', 'propranolol', 'amitriptyline'],
    preferredDocumentTypes: ['MEDICATION_LIST', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'dopamine_blocker_exposure', conceptType: CONCEPT_TYPES.MEDICATION_EXPOSURE,
    specialtyId: 'neurology',
    canonicalLabel: 'Dopamine blocker exposure',
    description: 'Dopamine-blocker exposure (drug-induced parkinsonism concern).',
    aliases: ['dopamine blocker', 'metoclopramide', 'prochlorperazine', 'antipsychotic', 'haloperidol'],
    preferredDocumentTypes: ['MEDICATION_LIST', 'CLINICAL_NOTE'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // DATA_ONLY — red flags (fully boundaried, NOT recognition-reachable; ready to
  // promote after review). No pattern + not in RED_FLAG group → never auto-links.
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'guillain_barre_ascending_weakness', conceptType: CONCEPT_TYPES.RED_FLAG,
    specialtyId: 'neurology',
    canonicalLabel: 'Ascending weakness (Guillain-Barre)',
    description: 'Rapidly progressive ascending weakness red flag (GBS concern).',
    positivePhrases: ['ascending weakness', 'Guillain-Barre', 'GBS', 'rapidly progressive weakness', 'ascending paralysis'],
    negativePhrases: ['no ascending weakness', 'stable weakness', 'no progressive weakness'],
    uncertaintyPhrases: ['possible ascending weakness', 'progressing weakness unclear'],
    excludedPhrases: ['chronic weakness', 'longstanding weakness', 'old deficit'],
    commonFalseMatches: ['generalized weakness', 'fatigue'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER', 'DISCHARGE_SUMMARY'],
  }),
  makeConcept({
    conceptId: 'acute_vision_loss', conceptType: CONCEPT_TYPES.RED_FLAG,
    specialtyId: 'neurology',
    canonicalLabel: 'Acute vision loss',
    description: 'Acute / severe vision loss red flag (optic neuritis concern).',
    positivePhrases: ['acute vision loss', 'sudden vision loss', 'severe vision loss', 'optic neuritis with vision loss'],
    negativePhrases: ['no vision loss', 'vision intact', 'denies vision loss'],
    uncertaintyPhrases: ['blurred vision', 'possible vision change'],
    excludedPhrases: ['chronic visual impairment', 'longstanding vision loss', 'stable cataract'],
    commonFalseMatches: ['mild blurring', 'refractive error'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER', 'CONSULT_NOTE'],
  }),
  makeConcept({
    conceptId: 'acute_spinal_cord_syndrome', conceptType: CONCEPT_TYPES.RED_FLAG,
    specialtyId: 'neurology',
    canonicalLabel: 'Acute spinal cord syndrome',
    description: 'Acute myelopathy / spinal cord syndrome red flag.',
    positivePhrases: ['spinal cord syndrome', 'sensory level', 'acute myelopathy', 'cord compression'],
    negativePhrases: ['no spinal cord syndrome', 'no sensory level', 'no myelopathy'],
    uncertaintyPhrases: ['possible myelopathy', 'unclear sensory level'],
    excludedPhrases: ['chronic myelopathy', 'old cord injury', 'longstanding deficit'],
    commonFalseMatches: ['back pain', 'radiculopathy'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER', 'DISCHARGE_SUMMARY'],
  }),
  makeConcept({
    conceptId: 'meningismus', conceptType: CONCEPT_TYPES.RED_FLAG,
    specialtyId: 'neurology',
    canonicalLabel: 'Meningismus',
    description: 'Meningismus (fever + neck stiffness / meningeal signs) red flag.',
    positivePhrases: ['meningismus', 'neck stiffness with fever', 'nuchal rigidity', 'meningeal signs'],
    negativePhrases: ['no neck stiffness', 'no meningismus', 'neck supple'],
    uncertaintyPhrases: ['possible meningismus', 'mild neck discomfort'],
    excludedPhrases: ['chronic neck stiffness', 'cervical spondylosis'],
    commonFalseMatches: ['neck pain', 'stiff neck from posture'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER', 'DISCHARGE_SUMMARY'],
  }),
  makeConcept({
    conceptId: 'raised_intracranial_pressure', conceptType: CONCEPT_TYPES.RED_FLAG,
    specialtyId: 'neurology',
    canonicalLabel: 'Raised intracranial pressure',
    description: 'Raised intracranial pressure / papilledema red flag.',
    positivePhrases: ['raised intracranial pressure', 'papilledema', 'raised ICP', 'signs of raised pressure'],
    negativePhrases: ['no papilledema', 'normal fundi', 'no raised pressure'],
    uncertaintyPhrases: ['possible papilledema', 'blurred disc margins unclear'],
    excludedPhrases: ['chronic papilledema', 'pseudopapilledema'],
    commonFalseMatches: ['optic disc drusen', 'tilted disc'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER', 'CONSULT_NOTE'],
  }),
];

// ============================================================================
// NEUROLOGY RECOGNITION PATTERNS (code-level RegExp, keyed by conceptId).
// EXACTLY the 8 LIVE concepts — tightened to avoid broad single-word matches.
// Flags 'i' only → stateless RegExp.test. DATA_ONLY concepts deliberately have
// NO pattern here (and no RECOGNITION_GROUPS membership in index.js) → inert.
//
// Tightening notes: headache excludes "head pain"/"pain"; seizure excludes
// "fit"/"loss of consciousness"/"collapse"; movement excludes bare "rigidity"/
// "shaking"; "ms" uses a word boundary (matches standalone MS only).
// ============================================================================
export const recognitionPatterns = {
  // FINDING-group LIVE (FIELD + OPTION)
  headache:              [/headache/i, /migraine/i, /cephalgia/i],
  seizure:               [/seizure/i, /convulsion/i, /epilep/i],
  neuropathy:            [/neuropath/i, /polyneuropath/i, /nerve pain/i],
  movement_disorder:     [/movement disorder/i, /parkinsonism/i, /bradykinesia/i, /\btremor\b/i, /involuntary movement/i],
  cognitive_decline:     [/cognitive (decline|impairment)/i, /memory (loss|decline)/i, /\bdementia\b/i],
  demyelinating_disease: [/demyelinat/i, /multiple sclerosis/i, /\bms\b/i, /clinically isolated syndrome/i, /optic neuritis/i],

  // RED_FLAG-group LIVE (binds the Pack red-flag label; matcher enforces positive_only)
  thunderclap_headache:  [/thunderclap/i, /worst headache of life/i, /maximal (intensity )?at onset/i, /sudden (severe|onset) headache/i],
  status_epilepticus:    [/status epilepticus/i, /prolonged seizure/i, /continuous seizure/i, /recurrent seizures without recovery/i],
};
